import { Plus, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { ConfigChip, ConfigChipField } from '@reflex/ui/components/agent/ConfigChip';
import type { PluginAttachmentValue } from '@/lib/types';
import type { AttachmentPickerProviderInfo } from '@reflex/plugin-api';
import type { ResolvedAttachment } from '@/lib/plugin-registry';
import { AttachmentPickerPanel, AttachmentSummaryLabel } from './AttachmentChipParts';
import { summaryOwnsIcon } from './attachment-chip-utils';

interface AttachmentChipProps {
  definition: ResolvedAttachment;
  value: PluginAttachmentValue | undefined;
  providers: AttachmentPickerProviderInfo[];
  connectProvider: (provider: string) => Promise<void>;
  connectingProvider: string | null;
  onConfigChange: (config: unknown | null) => void;
  onRemove: () => void;
}

/**
 * Renders one plugin attachment kind as a captioned `ConfigChipField` +
 * `ConfigChip` pair — the exact form used by the Advanced bar (Blueprint,
 * Size, Capabilities). The kind name sits in the caption above the chip; the
 * chip shows a dashed "Add" affordance until configured and an "Added" state
 * once a config exists, with the plugin's own `PickerComponent` (and a remove
 * control) hosted in the click popover. Every available kind gets its own
 * field so the bar reads as a row of named per-kind chips rather than a single
 * "+ Add" menu.
 */
export function AttachmentChip({
  definition,
  value,
  providers,
  connectProvider,
  connectingProvider,
  onConfigChange,
  onRemove,
}: AttachmentChipProps) {
  const Icon = definition.icon;
  const isConfigured = value !== undefined;

  // When configured, show a compact glanceable summary of what was attached
  // (e.g. the repo slug, or "GitHub +1" for MCP servers). Plugins that don't
  // provide a summary component fall back to a generic "Added" label.
  const configuredLabel = isConfigured ? (
    <AttachmentSummaryLabel
      definition={definition}
      config={value?.config ?? null}
      fallback="Added"
    />
  ) : (
    'Added'
  );
  const chipIcon = isConfigured ? (
    summaryOwnsIcon(definition) ? null : (
      <Icon className="h-3.5 w-3.5 shrink-0 text-foreground" />
    )
  ) : (
    <Plus className="h-3.5 w-3.5 shrink-0" />
  );

  return (
    <span className="contents" data-attachment-id={definition.id}>
      <ConfigChipField label={definition.label}>
        <ConfigChip
          ariaLabel={definition.label}
          label={isConfigured ? configuredLabel : 'Add'}
          dashed={!isConfigured}
          // Attachment pickers open hover-card-backed pills (e.g. the MCP
          // server pills); letting Radix auto-focus the first pill would pop
          // its hover card open the instant the popover appears.
          autoFocusContent={false}
          // Pickers render lists of named things (MCP servers, policies), and
          // the default 12rem floor squeezes those into one truncated word per
          // row. Written as a literal because Tailwind's scanner never sees a
          // class built at runtime.
          popoverClassName="min-w-[16rem]"
          icon={chipIcon}
          data-testid={`plugin-attachment-chip-${definition.id}`}
        >
          {() => (
            <div className="space-y-1.5 p-2">
              {/* The field caption above the chip already names the kind, so the
                  popover skips a redundant header and shows only the remove
                  control (when configured) alongside the plugin's picker. */}
              {isConfigured && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-muted-foreground"
                    aria-label={`Remove ${definition.label}`}
                    onClick={onRemove}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
              <AttachmentPickerPanel
                definition={definition}
                config={value?.config ?? null}
                onChange={onConfigChange}
                providers={providers}
                connectProvider={connectProvider}
                connectingProvider={connectingProvider}
              />
            </div>
          )}
        </ConfigChip>
      </ConfigChipField>
    </span>
  );
}
